import { useEffect, useState } from "react";
import { Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, POINT_CATS } from "../../lib/api";
import { Avatar, Empty, Field, Loader } from "../common";
import { AthletePicker } from "../AthletePicker";

const LaunchForm = ({ stage, level, athletes, regs, onDone }) => {
  const { settings } = useAuth();
  const [mode, setMode] = useState("dupla");
  const [regId, setRegId] = useState("");
  const [athlete, setAthlete] = useState("");
  const [cat, setCat] = useState("campeao");
  const [pts, setPts] = useState("");
  const def = settings?.points?.[cat];
  const submit = async () => {
    let user_ids = [];
    if (mode === "dupla") {
      const r = regs.find((x) => x.id === regId);
      if (!r) return toast.error("Selecione a dupla");
      user_ids = [r.player1_id, r.player2_id];
    } else if (mode === "atleta") {
      if (!athlete) return toast.error("Selecione o atleta");
      user_ids = [athlete];
    } else {
      user_ids = [...new Set(regs.flatMap((r) => [r.player1_id, r.player2_id]))];
      if (!user_ids.length) return toast.error("Nenhum inscrito neste nível");
    }
    try {
      const { data } = await api.post("/points", { user_ids, stage_id: stage.id, level, category: cat, points: pts === "" ? null : Number(pts) });
      toast.success(`${data.created} lançamento(s) criado(s)`);
      setPts("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="card-x space-y-4 p-5">
      <div className="h-display text-2xl">Lançar pontos</div>
      <div className="flex flex-wrap gap-2">
        {[["dupla", "Para uma dupla"], ["atleta", "Para um atleta"], ["todos", "Todos os inscritos"]].map(([v, l]) => (
          <button key={v} data-testid={`points-mode-${v}`} onClick={() => setMode(v)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${mode === v ? "bg-[#E4F222] text-black" : "border border-[#232B3E] text-[#94A3B8]"}`}>{l}</button>
        ))}
      </div>
      {mode === "dupla" && (
        <Field label="Dupla">
          <select data-testid="points-reg-select" className="inp" value={regId} onChange={(e) => setRegId(e.target.value)}>
            <option value="">Selecione</option>{regs.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>
      )}
      {mode === "atleta" && <Field label="Atleta"><AthletePicker testid="points-athlete" athletes={athletes} value={athlete} onChange={setAthlete} /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Categoria">
          <select data-testid="points-category-select" className="inp" value={cat} onChange={(e) => setCat(e.target.value)}>
            {Object.entries(POINT_CATS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Pontos"><input data-testid="points-value-input" type="number" className="inp" placeholder={`Padrão: ${def ?? ""}`} value={pts} onChange={(e) => setPts(e.target.value)} /></Field>
      </div>
      <button data-testid="points-submit-btn" onClick={submit} className="btn-lime w-full rounded-full py-3">Lançar</button>
    </div>
  );
};

export default function PointsTab({ stage, level, athletes }) {
  const { isAdmin } = useAuth();
  const [pts, setPts] = useState(null);
  const [regs, setRegs] = useState([]);
  const load = () => api.get("/points", { params: { stage_id: stage.id, level } }).then((r) => setPts(r.data));
  useEffect(() => {
    setPts(null);
    load();
    api.get(`/stages/${stage.id}/registrations`, { params: { level } }).then((r) => setRegs(r.data));
  }, [stage.id, level]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (id) => { await api.delete(`/points/${id}`); toast.success("Lançamento removido"); load(); };
  if (!pts) return <Loader />;
  return (
    <div className={`grid gap-6 ${isAdmin ? "lg:grid-cols-[380px_1fr]" : ""}`}>
      {isAdmin && <LaunchForm stage={stage} level={level} athletes={athletes} regs={regs} onDone={load} />}
      <div>
        {pts.length === 0 ? <Empty icon={Star} title="Sem pontos" text="Nenhum ponto lançado neste nível ainda." /> : (
          <div className="space-y-2">
            {pts.map((p) => (
              <div key={p.id} data-testid={`point-${p.id}`} className="card-x flex items-center gap-3 p-3">
                <Avatar user={p.user} size={36} />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-semibold">{p.user?.name}</div><div className="text-xs text-[#94A3B8]">{p.category_label}</div></div>
                <div className="font-display text-2xl font-bold text-[#E4F222]">+{p.points}</div>
                {isAdmin && <button data-testid={`delete-point-${p.id}`} onClick={() => remove(p.id)} className="p-2 text-[#64748B] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
